/* eslint-disable no-console */
import { cookiesService } from '@libs/utils/cookies'
import { BILLING_API_BASE_URL, HTTP_PROTOCOL } from '@libs/utils/environments'
import type { InternalAxiosRequestConfig } from 'axios'

import { defaultParamsSerializer } from '../../internals'

import { HttpModel } from './httpModel.class'
import type { HttpServiceEndpoints } from './interfaces/HttpClient.interface'

export class HttpDirectBilling extends HttpModel {
  private tokenRefreshPromise: Promise<string | null> | null = null

  constructor(private snapAppCore: HttpServiceEndpoints) {
    super(
      {
        baseURL: `${HTTP_PROTOCOL}://${BILLING_API_BASE_URL}/api`,
        paramsSerializer: defaultParamsSerializer,
        withCredentials: true
      },
      HttpModel.contentTypes.json
    )
  }

  private async fetchAndSetNewToken(): Promise<string | null> {
    try {
      const { data } = await this.snapAppCore.get<{ jwt: string }>(
        '/accounts/billing_jwt'
      )
      this.setBillingJwt(data.jwt)
      return data.jwt
    } catch (error) {
      return null
    } finally {
      this.tokenRefreshPromise = null
    }
  }

  protected override getRequestInterceptors() {
    return [this.authInterceptor.bind(this)]
  }

  protected override getResponseInterceptors() {
    return [this.handleUnauthorizedError.bind(this)]
  }

  protected handleUnauthorizedError(error: any) {
    const token = cookiesService.readBillingJwt()

    if (token && error.response?.status === 401) {
      cookiesService.removeAuthCookies()
      window.location.href = '/'
    }
  }

  protected async authInterceptor(config: InternalAxiosRequestConfig<any>) {
    let token = cookiesService.readBillingJwt()

    if (!token) {
      if (!this.tokenRefreshPromise) {
        this.tokenRefreshPromise = this.fetchAndSetNewToken()
      }
      const newToken = await this.tokenRefreshPromise
      if (newToken) {
        token = newToken
      } else {
        throw new Error('Failed to obtain billing JWT token')
      }
    }

    config.headers.Authorization = `Bearer ${token}`
    return config
  }

  public setBillingJwt(token: string) {
    cookiesService.writeBillingJwt(token)
    this.updateHeaders({ Authorization: `Bearer ${token}` })
  }
}
